import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, UserPlus, Smartphone, FileUp, ScanSearch, FileCog,
  Send, Landmark, CreditCard, Monitor, Check, PenLine,
} from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    icon: UserPlus,
    devices: ["Ordinateur", "Mobile"],
    title: "Créez votre compte en 30 secondes",
    steps: [
      "Cliquez sur « Lancer l'app » puis « Pas encore de compte ? Créer un compte »",
      "Saisissez votre e-mail et un mot de passe (8 caractères minimum) — ou passez par « Continuer avec Google »",
      "Vous arrivez directement sur votre tableau de bord, sur le web comme sur l'app mobile",
    ],
  },
  {
    n: "02",
    icon: Smartphone,
    devices: ["Mobile"],
    title: "Installez l'app sur votre téléphone",
    steps: [
      "iPhone : ouvrez le site dans Safari → bouton Partager → « Sur l'écran d'accueil »",
      "Android : ouvrez le site dans Chrome → menu ⋮ → « Installer l'application »",
      "L'icône FacturXpress apparaît sur votre écran d'accueil : l'app se lance en plein écran, comme une application native",
    ],
  },
  {
    n: "03",
    icon: FileUp,
    devices: ["Ordinateur", "Mobile"],
    title: "Importez votre première facture PDF",
    video: "/demos/demo-import.mp4",
    steps: [
      "Ouvrez « Importer » dans le menu",
      "Ordinateur : glissez-déposez le PDF dans la zone prévue. Mobile : touchez la zone et choisissez le fichier",
      "L'IA analyse le document : vendeur, acheteur, lignes, TVA et octroi de mer sont extraits automatiquement",
    ],
  },
  {
    n: "04",
    icon: PenLine,
    devices: ["Ordinateur", "Mobile"],
    title: "Ou créez une facture de zéro",
    video: "/demos/demo-creation.mp4",
    steps: [
      "Menu « Factures » → « Nouvelle facture » : client, date et région de vente",
      "Gagnez du temps avec les modèles par métier (BTP, Restauration, Services, Consulting) qui pré-remplissent les lignes",
      "Totaux HT / TVA / TTC calculés en direct, octroi de mer automatique en DOM-TOM — la facture suit ensuite le même cycle : Factur-X, PDP, archivage",
    ],
  },
  {
    n: "05",
    icon: ScanSearch,
    devices: ["Ordinateur", "Mobile"],
    title: "Vérifiez et corrigez l'extraction",
    video: "/demos/demo-detail.mp4",
    steps: [
      "La facture importée s'ouvre avec toutes les données détectées",
      "Un doute ? Cliquez « Autocorriger (IA) » pour une vérification automatique",
      "Vous pouvez aussi modifier manuellement le numéro et les dates via « Modifier »",
    ],
  },
  {
    n: "06",
    icon: FileCog,
    devices: ["Ordinateur", "Mobile"],
    title: "Convertissez en Factur-X",
    video: "/demos/demo-convert.mp4",
    steps: [
      "Cliquez « Convertir Factur-X » sur la facture",
      "Le fichier conforme EN 16931 est généré (Factur-X, UBL ou CII)",
      "Téléchargez-le depuis le panneau « Fichiers » de la facture",
    ],
  },
  {
    n: "07",
    icon: Send,
    devices: ["Ordinateur", "Mobile"],
    title: "Envoyez via votre PDP",
    steps: [
      "Paramètres → Connexion PDP : renseignez votre plateforme puis « Tester la connexion »",
      "Sur la facture : « Envoyer via PDP » transmet au portail choisi",
      "Suivez les statuts en temps réel (« Rafraîchir statut ») ; pour les factures reçues : « Accepter » ou « Refuser »",
    ],
  },
  {
    n: "08",
    icon: Landmark,
    devices: ["Ordinateur", "Mobile"],
    title: "Octroi de mer DOM-TOM",
    steps: [
      "Les factures Guadeloupe, Martinique, Guyane, La Réunion et Mayotte affichent le badge « Octroi de mer »",
      "Le détail ligne par ligne (taux, montant) apparaît dans le tableau des lignes",
      "Les totaux HT / TVA / Octroi / TTC sont séparés clairement dans le panneau Totaux",
    ],
  },
  {
    n: "09",
    icon: CreditCard,
    devices: ["Ordinateur", "Mobile"],
    title: "Choisissez votre formule",
    steps: [
      "Commencez gratuitement : 3 factures offertes à l'inscription, sans carte bancaire",
      "Menu « Abonnement » : comparez Starter (9€), Pro (19€) et Entreprise (49€)",
      "Payez par PayPal ou carte bancaire — résiliable à tout moment",
      "Votre quota de conversions est visible sur le tableau de bord",
    ],
  },
];

const DeviceTag = ({ label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
    {label === "Mobile" ? <Smartphone className="h-3 w-3" /> : <Monitor className="h-3 w-3" />}
    {label}
  </span>
);

export default function Guide() {
  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="guide-page">
      <div className="noise-overlay" />

      <header className="fixed top-0 inset-x-0 z-50 glass-panel border-x-0 border-t-0" data-testid="guide-nav">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="guide-logo" className="flex items-center gap-2.5">
            <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[13px] font-extrabold text-[#050505]">FX</span>
            <span className="font-display text-sm tracking-tight">Factur<span className="text-[#00D4FF]">Xpress</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="guide-back-home" className="text-sm text-zinc-400 hover:text-white transition-colors duration-200">Accueil</Link>
            <Link
              to="/connexion"
              data-testid="guide-cta"
              className="rounded-full bg-white text-[#050505] text-sm font-semibold px-5 py-2 hover:bg-[#00D4FF] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              Lancer l'app
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-36 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="overline-label mb-4">Guide de démarrage</p>
          <h1 className="font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.05]" data-testid="guide-title">
            Prenez en main <span className="font-extrabold text-[#00D4FF]">FacturXpress.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Huit étapes courtes, valables sur ordinateur, iPhone et Android. De la création de votre
            compte jusqu'à l'envoi de votre première facture Factur-X via votre PDP.
          </p>
        </motion.div>

        <div className="mt-20 space-y-16">
          {CHAPTERS.map((c, i) => (
            <motion.section
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="grid md:grid-cols-12 gap-6 items-start"
              data-testid={`guide-chapter-${c.n}`}
            >
              <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                <span className="font-display text-6xl md:text-7xl font-light text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.25)] select-none">
                  {c.n}
                </span>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#00D4FF] border border-[#2563EB]/30">
                  <c.icon className="h-5 w-5" />
                </span>
              </div>
              <div className="md:col-span-9 border-l border-white/10 pl-6 md:pl-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {c.devices.map((d) => <DeviceTag key={d} label={d} />)}
                </div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight">{c.title}</h2>
                <ol className="mt-5 space-y-3">
                  {c.steps.map((s, k) => (
                    <li key={k} className="flex items-start gap-3 text-zinc-400 leading-relaxed">
                      <Check className="h-4 w-4 mt-1 text-[#34C759] shrink-0" />
                      {s}
                    </li>
                  ))}
                </ol>
                {c.video && (
                  <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 bg-black shadow-[0_8px_32px_rgba(0,0,0,0.5)]" data-testid={`guide-video-wrap-${c.n}`}>
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      data-testid={`guide-video-${c.n}`}
                      className="w-full h-auto"
                    >
                      <source src={c.video} type="video/mp4" />
                      <source src={c.video.replace(".mp4", ".webm")} type="video/webm" />
                    </video>
                    <p className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-t border-white/5">
                      Démonstration réelle de l'application
                    </p>
                  </div>
                )}
              </div>
            </motion.section>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-24 rounded-3xl border border-white/10 bg-[#0A0E17] p-10 md:p-14 relative overflow-hidden"
          data-testid="guide-final-cta"
        >
          <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_100%,rgba(37,99,235,0.2),transparent_70%)]" />
          <div className="relative">
            <h2 className="font-display font-light tracking-tighter text-3xl md:text-4xl">Prêt à facturer ?</h2>
            <p className="mt-4 text-zinc-400 max-w-lg">
              Créez votre compte et importez votre première facture en moins de deux minutes.
            </p>
            <Link
              to="/connexion"
              data-testid="guide-final-cta-button"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-8 py-4 text-sm font-bold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-300"
            >
              Commencer maintenant
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">© 2026 FacturXpress — Web · iOS · Android</p>
          <Link to="/" data-testid="guide-footer-home" className="text-sm text-zinc-500 hover:text-white transition-colors duration-200">Retour à l'accueil</Link>
        </div>
      </footer>
    </div>
  );
}
